import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, MessageCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, AIPanel } from '@/components';
import { useSessionStore } from '@/stores/sessionStore';
import { useAuth } from '@/auth/AuthProvider';
import { useJoinQueue, useMyQueuePosition, useUpdateQueueDoc } from '@/firebase/hooks';
import { useAgentChat } from '@/firebase/useAgentChat';
import IntakeChecklist from './IntakeChecklist';
import { useTelehealthConsent } from './useTelehealthConsent';
import SelfScreenPanel from './SelfScreenPanel';
import { useUiModeFlags } from '@/auth/useUiMode';

// TV-P2-05 will write each completion back to the Firestore doc; until
// then the checklist mirrors whatever intakeProgress map is on the doc
// (defaults to all-false from useJoinQueue's first write).
const CHECKLIST_DEFS = [
  { key: 'consent', label: 'Consent form signed' },
  { key: 'questionnaire', label: 'Intake questionnaire complete' },
  { key: 'av', label: 'Camera + microphone tested' },
];

// Visit prep suggestion seeds — these are pre-canned question
// starters surfaced as one-tap buttons. The user can also free-type.
// TV-P7-11b: clicking a suggestion now sends the question through
// useAgentChat so the real Gemini agent answers (was setTimeout fake).
const VISIT_PREP_SUGGESTIONS = [
  {
    icon: '🩺',
    question: 'How have your blood sugar levels been recently?',
    category: 'Diabetes Management',
  },
  {
    icon: '💊',
    question: 'Are you taking all your medications as prescribed?',
    category: 'Medication Adherence',
  },
  {
    icon: '📊',
    question: 'Have you noticed any changes in your blood pressure at home?',
    category: 'Vital Signs',
  },
  {
    icon: '👁️',
    question: 'When was your last eye exam?',
    category: 'Care Gaps',
  },
];

export default function PatientWaitingRoom() {
  // TV-LM-5 — In lite mode, drop the right column entirely (SelfScreenPanel
  // PRAPARE intake + AI visit-prep prompts + AI chat). The left column
  // (queue position, provider info, consent + AV pre-test) stays — those
  // are HIPAA + UX-critical and must not be silently skipped.
  const { isLite } = useUiModeFlags();
  const [inputValue, setInputValue] = useState('');

  // TV-P2-03 + TV-P2-04: realtime queue presence.
  const { user } = useAuth();
  const tenantId = useSessionStore((s) => s.tenantId);
  const fhirPatientId = useSessionStore(
    (s) => s.currentPatient || s.smartContext?.patientId,
  );
  // Doc key is the Firebase Auth UID (== KC sub). The FHIR patient id
  // goes into the doc body so the provider's queue can still
  // dedupe against today's Appointment list. The Firestore rule's
  // `request.auth.uid == patientId` predicate requires the path key
  // to match the auth uid — using the FHIR id here would always 403.
  //
  // STAB-04: explicitly use user.sub (KC sub). Pre-STAB-04 we used
  // user.id, which fell back to KC sub when fhirUser was empty but
  // resolved to the FHIR Patient.id once STAB-03 started populating
  // fhirUser → permission denied on queue + ai-agent reads.
  const uid = user?.sub || user?.id || null;
  const patientId = fhirPatientId || uid;

  // Write own queue doc on mount; mark left on unmount.
  const { joined: queueJoined } = useJoinQueue(uid, tenantId, {
    patientId: fhirPatientId || null,
    patientName: user?.name,
    chiefComplaint: '', // TV-P2-05 will collect via intake form
  });

  // Listen to own doc + the tenant's waiting list to compute position.
  const {
    myDoc,
    position: queuePosition,
    estimatedMinutes: estimatedWait,
  } = useMyQueuePosition(uid, tenantId);

  // Provider display name comes from the SMART launch when present;
  // PatientPanel-like FHIR lookup of Practitioner is parked for P3
  // (the visit page already has access to the practitioner via the
  // Encounter participant).
  const providerName = 'Your provider';

  // Map the Firestore intakeProgress to the checklist display.
  const checklist = useMemo(() => {
    const progress = myDoc?.intakeProgress || {};
    return CHECKLIST_DEFS.map((def) => ({
      ...def,
      completed: !!progress[def.key],
    }));
  }, [myDoc]);
  const completedItems = checklist.filter((i) => i.completed).length;

  // TV-P2-05/06: patient updates flow through this single patch fn.
  // Keyed by auth uid (doc key), not FHIR id — matches useJoinQueue.
  const patchQueueDoc = useUpdateQueueDoc(uid, tenantId);

  // TV-P7-11b — pre-visit AI companion chat.
  //
  // The waiting room has no real Encounter yet (one is created on
  // /visit Start by the provider). To still persist a chat history,
  // we mint a deterministic synthetic id: `pre-visit-{authUid}`.
  // This:
  //   - Stays stable across page refreshes + reconnects.
  //   - Is one-thread-per-patient (continues across visits, which
  //     fits the "patient health companion" use case more than a
  //     fresh thread per appointment would).
  //   - Is gated by the firestore rule:
  //       /ai-agent/{encounterId} read-only if
  //         encounterId == 'pre-visit-' + request.auth.uid
  //     — so no other patient can peek even with the path guessed.
  //
  // useAgentChat already pulls tenantId + patientId from session
  // store; we just hand it the encounter id.
  const preVisitEncounterId = uid ? `pre-visit-${uid}` : null;
  const {
    messages: agentMessages,
    sendMessage: sendAgentMessage,
    sending: agentSending,
    error: agentError,
  } = useAgentChat(preVisitEncounterId);

  // Auto-scroll chat as new turns land.
  const chatScrollRef = useRef(null);
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [agentMessages.length, agentSending]);

  // TV-P4-02/03: telehealth Consent on file?
  // - existingConsent !== null → auto-flip intakeProgress.consent.
  // - existingConsent === null → IntakeChecklist surfaces typed-name
  //   capture and POSTs a Consent on submit (recordConsent).
  const {
    existingConsent,
    recordConsent,
    isRecording: isRecordingConsent,
    isLoading: isLoadingConsent,
  } = useTelehealthConsent(fhirPatientId);

  // One-shot: once an existing Consent is found AND the queue doc
  // exists, flip the consent flag if it's not already green.
  const consentBackfillRef = useRef(false);
  useEffect(() => {
    if (consentBackfillRef.current) return;
    if (!existingConsent || !myDoc) return;
    if (myDoc.intakeProgress?.consent) return;
    consentBackfillRef.current = true;
    patchQueueDoc({ 'intakeProgress.consent': true }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[PatientWaitingRoom] consent backfill failed:', err);
      consentBackfillRef.current = false; // allow retry next render
    });
  }, [existingConsent, myDoc, patchQueueDoc]);

  // TV-P7-11b — both the free-text input AND the suggestion buttons
  // route through useAgentChat. The hook persists user+assistant
  // turns to Firestore; the snapshot listener picks them up here.
  // We deliberately don't optimistic-write the user turn — the
  // Cloud Function writes it before calling Gemini so refreshes
  // don't lose the question.
  const handleSendMessage = async (e) => {
    e?.preventDefault?.();
    const text = inputValue.trim();
    if (!text || agentSending) return;
    setInputValue('');
    await sendAgentMessage(text);
  };

  const handleQuestionClick = async (question) => {
    if (agentSending) return;
    await sendAgentMessage(question);
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Your Waiting Room</h1>
        <p className="text-slate-400">Get ready for your appointment with {providerName}</p>
      </div>

      <div className={isLite ? 'max-w-md mx-auto' : 'grid grid-cols-3 gap-6'}>
        {/* Left Column: Queue Info + Checklist */}
        <div className={isLite ? 'space-y-6' : 'col-span-1 space-y-6'}>
          {/* Queue Position Card */}
          <Card className="bg-gradient-to-br from-blue-900 to-slate-900 border-blue-800">
            <div className="text-center py-8">
              <p className="text-slate-300 text-sm uppercase tracking-wider mb-2">Queue Position</p>
              <p className="text-6xl font-bold text-white mb-4">
                {queuePosition != null ? queuePosition : '—'}
              </p>
              <div className="space-y-2">
                {estimatedWait != null && (
                  <p className="text-blue-300 font-medium">
                    {estimatedWait === 0
                      ? "You're next"
                      : `Est. wait: ${estimatedWait} minute${estimatedWait === 1 ? '' : 's'}`}
                  </p>
                )}
                <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
                  <div
                    className={`w-2 h-2 rounded-full ${queueJoined ? 'bg-green-500 animate-pulse' : 'bg-slate-500'}`}
                  />
                  {queueJoined ? 'Connected to provider queue' : 'Joining queue…'}
                </div>
              </div>
            </div>
          </Card>

          {/* Provider Info */}
          <Card className="bg-slate-900 border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                SM
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Your Provider</p>
                <p className="text-white font-semibold">{providerName}</p>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Board-certified in Family Medicine with 15+ years of experience in chronic disease
              management.
            </p>
          </Card>

          {/* Diagnostic banner — surface the silent-fail case where the
              KC token didn't carry a sub claim, so uid is null and no
              writes are ever attempted. */}
          {!uid && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <p className="text-sm font-semibold text-amber-200 mb-1">
                Connecting to your account…
              </p>
              <p className="text-xs text-amber-200/80">
                Your sign-in is still resolving. If this banner sticks for
                more than ~10 seconds, the launch token is missing a user
                id (Keycloak <code>sub</code> claim). Re-launch this visit
                from your patient portal.
              </p>
            </Card>
          )}

          {/* Pre-Visit Checklist — interactive (TV-P2-05 + TV-P2-06).
              Each step writes back to the patient's queue doc; the
              provider's onSnapshot listener sees green checks land live. */}
          <Card className="bg-slate-900 border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Pre-Visit Checklist</h3>
              <span className="text-xs font-mono text-blue-400">
                {completedItems}/{checklist.length}
              </span>
            </div>
            <IntakeChecklist
              intakeProgress={myDoc?.intakeProgress}
              chiefComplaint={myDoc?.chiefComplaint}
              onPatch={patchQueueDoc}
              existingConsent={existingConsent}
              isLoadingConsent={isLoadingConsent}
              isRecordingConsent={isRecordingConsent}
              recordConsent={recordConsent}
              consentDisplayName={user?.name}
            />
          </Card>
        </div>

        {/* Right Column: Self-screen + AI Panel + Chat — full mode only. */}
        {!isLite && (
        <div className="col-span-2 space-y-6">
          {/* TV-P5-09 — optional self-screen Questionnaire (PRAPARE).
              Answers persist as a FHIR QuestionnaireResponse + derived
              Observations the provider sees on chart open. */}
          <SelfScreenPanel
            patientId={fhirPatientId}
            questionnaireId="prapare-loinc"
          />

          {/* AI Visit Preparation Panel */}
          <AIPanel
            title="Visit Preparation"
            subtitle="Personalized questions to prepare for your appointment"
          >
            <div className="space-y-2">
              {VISIT_PREP_SUGGESTIONS.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuestionClick(suggestion.question)}
                  disabled={agentSending || !preVisitEncounterId}
                  className="w-full text-left p-3 bg-slate-800/50 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-700 rounded-lg transition-all duration-200 group"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">{suggestion.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white group-hover:text-blue-300 transition-colors">
                        {suggestion.question}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{suggestion.category}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </AIPanel>

          {/* Chat Section */}
          <Card className="bg-slate-900 border-slate-800 flex flex-col h-[400px]">
            <div className="p-4 border-b border-slate-800 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-400" />
              <h3 className="font-semibold text-white">AI Chat</h3>
            </div>

            {/* Messages Area — TV-P7-11b reads from Firestore via
                useAgentChat(`pre-visit-${uid}`). Shows an empty-state
                primer when the thread has no turns yet. */}
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {agentError && (
                <div className="text-xs text-red-300 p-3 bg-red-500/10 rounded-lg border border-red-500/30 flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Chat error: {agentError.message}</span>
                </div>
              )}
              {!agentError && agentMessages.length === 0 && (
                <div className="text-center text-xs text-slate-500 italic py-6 px-4">
                  Hi! I'm your AI health companion. Ask me anything you'd
                  like to discuss with your provider — or tap a suggestion
                  above. Your thread is private to you.
                </div>
              )}
              {agentMessages.map((message) => {
                const isUser = message.role === 'user';
                const ts = message.createdAt?.toDate?.()
                  ?? (message.createdAt?.seconds
                    ? new Date(message.createdAt.seconds * 1000)
                    : null);
                const tsLabel = ts
                  ? ts.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '';
                return (
                  <div
                    key={message.id}
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs px-4 py-2 rounded-lg ${
                        isUser
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-800 text-slate-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                      {tsLabel && (
                        <p
                          className={`text-xs mt-1 ${
                            isUser ? 'text-blue-200' : 'text-slate-500'
                          }`}
                        >
                          {tsLabel}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              {agentSending && (
                <div className="flex justify-start">
                  <div className="max-w-xs px-4 py-2 rounded-lg bg-slate-800/60 text-slate-300 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="text-xs italic">Thinking…</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-slate-800">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={agentSending || !preVisitEncounterId}
                  placeholder={
                    preVisitEncounterId
                      ? 'Ask a health question...'
                      : 'Signing in…'
                  }
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={agentSending || !inputValue.trim() || !preVisitEncounterId}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg p-2 transition-colors flex-shrink-0"
                >
                  {agentSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </form>
            </div>
          </Card>
        </div>
        )}
      </div>
    </div>
  );
}
